import { useMemo, useEffect } from 'react';
import {
  getStorage,
  setStorage,
  removeStorage,
  useLocalStorage,
} from '@syntra/ui/hooks/use-local-storage';

import { LOCAL_STORAGE_KEYS } from 'src/config-global';
import { useGetAPIConfigs } from 'src/api/page-and-config';

import { APIConfigContext } from './api-config-context';

// ----------------------------------------------------------------------

const STORAGE_KEY = LOCAL_STORAGE_KEYS.config;

/**
 * Caches that hold server-derived data and must be invalidated when the API
 * config version changes. `auth` is intentionally excluded — we never log
 * the user out as a side-effect of a version bump.
 */
const CACHE_KEYS_TO_INVALIDATE = [
  LOCAL_STORAGE_KEYS.config,
  LOCAL_STORAGE_KEYS.permissions,
  LOCAL_STORAGE_KEYS.feature_flags,
] as const;

type APIConfigProviderProps = {
  children: React.ReactNode;
};

export function APIConfigProvider({ children }: APIConfigProviderProps) {
  const { state, update, reset } = useLocalStorage(STORAGE_KEY, {});
  const { config, configLoading } = useGetAPIConfigs();

  useEffect(() => {
    if (configLoading || !config) return;

    const incomingVersion = config.version ?? null;

    if (incomingVersion !== null) {
      const storedVersion = getStorage(LOCAL_STORAGE_KEYS.config_version);

      if (storedVersion !== incomingVersion) {
        // New config version detected — wipe all server-derived caches so
        // PermissionsProvider and FeatureFlagsProvider re-fetch fresh data.
        CACHE_KEYS_TO_INVALIDATE.forEach(removeStorage);
        setStorage(LOCAL_STORAGE_KEYS.config_version, incomingVersion);
      }
    }

    update(STORAGE_KEY, config);
  }, [config, configLoading, update]);

  const memoizedValue = useMemo(
    () => ({
      ...state,
      update,
      reset,
    }),
    [state, update, reset]
  );

  return <APIConfigContext.Provider value={memoizedValue}>{children}</APIConfigContext.Provider>;
}
